import { useSignal } from '@preact/signals';
import { useEffect, useRef } from 'preact/hooks';
import type { Again, CandidateView, MeetupSnapshot } from '../../shared/schemas.ts';
import type { TravelMode } from '../../src/domain/engine.ts';
import type { LatLon } from '../../src/domain/geo.ts';
import {
  Avatar,
  Card,
  Chips,
  FeynmanNote,
  GpsButton,
  PlaceSearch,
  ShareButton,
  Sheet,
  Spinner,
} from '../components/ui.tsx';
import { directionsUrl, nativeMapsName, nativeMapsUrl, type Place, reverse } from '../geo.ts';
import { api, config, fail, mins, modeEmoji, savedMode, savedName, toast } from '../lib.ts';
import { useLive } from '../live.ts';
import { MeetupMap } from '../map/meetupMap.ts';
import { applyLocal } from '../optimistic.ts';

const GMAPS: Record<TravelMode, string> = { foot: 'walking', bike: 'bicycling', car: 'driving', transit: 'transit' };
type Call = (path: string, method: string, body?: unknown) => Promise<void>;
/** Label of a map tap while its address is still being looked up. */
const LOOKING_UP = '…';
const located = (p: Place) => ({ lat: p.lat, lon: p.lon, label: p.label, source: p.source ?? 'address' });

export function MeetupPage({ id }: { id: string }) {
  const live = useLive<MeetupSnapshot>(`/api/meetups/${id}/events`);
  const votes = useSignal(new Map<string, number>());
  const hover = useSignal<string | null>(null);
  const tapped = useSignal<Place | null>(null);
  const joinSpot = useSignal<Place | null>(null);
  const mapRef = useRef<MeetupMap | null>(null);
  const mapEl = useRef<HTMLDivElement>(null);

  const raw = live.snap.value;
  const s = raw?.me && votes.value.size ? applyLocal(raw, raw.me.id, votes.value) : raw;

  useEffect(() => {
    if (!mapEl.current || mapRef.current) return;
    mapRef.current = new MeetupMap(mapEl.current, {
      onPick: async (p) => {
        const cur = live.snap.peek();
        if (!cur) return;
        if (!cur.me) {
          // Not joined yet: a tap sets where you're coming from.
          joinSpot.value = await reverse(p);
          return;
        }
        tapped.value = { ...p, label: LOOKING_UP };
        tapped.value = await reverse(p);
      },
      onSelect: (cid) => document.getElementById(`c-${cid}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
      onHover: (cid) => {
        hover.value = cid;
      },
    });
    return () => {
      mapRef.current?.destroy();
      mapRef.current = null;
    };
  }, [raw != null]);
  useEffect(() => {
    if (s) mapRef.current?.update(s, hover.value);
  }, [s, hover.value]);
  useEffect(() => {
    mapRef.current?.setPin(tapped.value ?? joinSpot.value);
  }, [tapped.value, joinSpot.value]);
  useEffect(() => {
    if (raw) document.title = `${raw.title} · Midway`;
  }, [raw?.title]);

  const call: Call = (path, method, body) =>
    api<MeetupSnapshot>(`/api/meetups/${id}${path}`, { method, body }).then(live.accept);

  if (live.error.value) {
    return (
      <Card>
        <h2>Link not found</h2>
        <p class="muted">Check the link, or make a new one.</p>
        <a class="btn primary" href="/">
          New link
        </a>
      </Card>
    );
  }

  const vote = async (cid: string, value: -1 | 0 | 1) => {
    votes.value = new Map(votes.value).set(cid, value); // instant, re-scored locally
    try {
      await call('/votes', 'PUT', { candidateId: cid, value });
    } catch (e) {
      fail(e);
    }
    const next = new Map(votes.value);
    next.delete(cid);
    votes.value = next;
  };

  return (
    <div class="meetup">
      {s && <Header s={s} />}
      <div class="layout">
        <div class="mapcol">
          <div ref={mapEl} id="map" />
          {s && <FocusBar s={s} />}
        </div>
        <div class="side stack">
          {!s ? (
            <Card>
              <Spinner /> Loading…
            </Card>
          ) : (
            <>
              {!s.me && (
                <JoinCard
                  s={s}
                  spot={joinSpot.value}
                  onSpot={(p) => {
                    joinSpot.value = p;
                  }}
                  onJoined={(snap) => {
                    joinSpot.value = null;
                    live.accept(snap);
                    live.reconnect();
                  }}
                />
              )}
              {s.me && s.me.lat == null && s.status === 'open' && <WhereCard s={s} call={call} />}
              {s.rate && !s.rate.mine && <RateCard rate={s.rate} call={call} />}
              {s.status === 'decided' && <Decided s={s} call={call} />}
              {s.status === 'open' && s.me && <ProposeBox s={s} call={call} onFly={(p) => mapRef.current?.flyTo(p)} />}
              <Places s={s} vote={vote} call={call} hover={hover} />
              <People s={s} call={call} />
              <ShareCard s={s} call={call} />
            </>
          )}
        </div>
      </div>
      {s && (
        <MapSheet
          place={tapped.value}
          s={s}
          onClose={() => (tapped.value = null)}
          onPropose={(name, p) => {
            tapped.value = null;
            const address = p.label === LOOKING_UP ? null : p.label;
            call('/candidates', 'POST', { name, lat: p.lat, lon: p.lon, address }).then(
              () => toast(`Proposed ${name}`),
              fail,
            );
          }}
          onStartHere={(p) => {
            tapped.value = null;
            call('/members/me', 'PATCH', { location: { ...located(p), source: 'map' } }).then(
              () => toast('Starting point updated'),
              fail,
            );
          }}
        />
      )}
    </div>
  );
}

function Header({ s }: { s: MeetupSnapshot }) {
  const act = config.value?.activities.find((a) => a.id === s.activity);
  const leader = s.candidates[0];
  const me = s.me;
  const winner = s.candidates.find((c) => c.id === s.decidedId);
  return (
    <header class="status-head">
      <h1>
        {act?.emoji ?? '📍'} {s.title}
      </h1>
      <div class="status-line">
        {winner ? (
          <span>
            🎉 <b>{winner.name}</b>
            {me && winner.times[me.id] != null && (
              <span class="muted">
                {' '}
                · {mins(winner.times[me.id])} for you {modeEmoji(me.mode)}
              </span>
            )}
          </span>
        ) : leader ? (
          <span>
            Leading: <b>{leader.name}</b>
            {me && leader.times[me.id] != null && <span class="muted"> · {mins(leader.times[me.id])} for you</span>}
          </span>
        ) : (
          <span class="muted">Everyone adds where they start from, then propose and vote.</span>
        )}
      </div>
    </header>
  );
}

function FocusBar({ s }: { s: MeetupSnapshot }) {
  if (s.decidedId) return null;
  return (
    <div class="focusbar">
      {s.focus ? (
        <span>
          <b>Best area{s.focus.label ? `: ${s.focus.label}` : ''}</b>
          <span class="muted"> · {s.focus.from === 'fair-middle' ? 'fair middle' : 'travel times + votes'}</span>
        </span>
      ) : (
        <span class="muted">Add where you are to see the fair middle.</span>
      )}
      {(s.busy.times || s.busy.suggestions) && (
        <span class="muted small">
          <Spinner /> {s.busy.times ? 'checking travel times' : 'finding places'}
        </span>
      )}
      {s.me && s.status === 'open' && <span class="hint">Tap the map to suggest a spot</span>}
      {s.note && <span class="note">{s.note}</span>}
    </div>
  );
}

function JoinCard({
  s,
  spot,
  onSpot,
  onJoined,
}: {
  s: MeetupSnapshot;
  spot: Place | null;
  onSpot: (p: Place) => void;
  onJoined: (s: MeetupSnapshot) => void;
}) {
  const name = useSignal(savedName.get());
  const mode = useSignal<TravelMode>(savedMode.get() ?? config.value!.modes[0].id);
  const busy = useSignal(false);
  const submit = async (e: Event) => {
    e.preventDefault();
    if (!name.value.trim()) return toast('Add your name');
    busy.value = true;
    try {
      savedName.set(name.value.trim());
      savedMode.set(mode.value);
      onJoined(
        await api<MeetupSnapshot>(`/api/meetups/${s.id}/members`, {
          method: 'POST',
          body: { name: name.value.trim(), travelMode: mode.value, location: spot ? located(spot) : null },
        }),
      );
    } catch (err) {
      fail(err);
      busy.value = false;
    }
  };
  const others = s.members;
  return (
    <Card class="join">
      <form class="stack" onSubmit={submit}>
        <div>
          <h2>Join {s.title}</h2>
          {others.length > 0 && (
            <div class="row join-preview">
              <span class="voters">
                {others.slice(0, 6).map((m) => (
                  <Avatar key={m.id} name={m.name} color={m.color} small />
                ))}
              </span>
              <span class="small muted">
                {others
                  .map((m) => m.name)
                  .slice(0, 3)
                  .join(', ')}
                {others.length > 3 ? ` +${others.length - 3}` : ''}
              </span>
            </div>
          )}
        </div>
        <input
          type="text"
          placeholder="Your name"
          maxLength={40}
          value={name.value}
          onInput={(e) => (name.value = e.currentTarget.value)}
        />
        <div class="stack tight">
          <GpsButton label="📍 I'm here (GPS)" auto onLocated={onSpot} />
          <PlaceSearch placeholder="…or type where you start from" near={s.focus} onPick={onSpot} />
          <span class="hint">{spot ? `📍 ${spot.label}` : 'You can also tap the map.'}</span>
        </div>
        <Chips options={config.value!.modes} value={mode.value} onChange={(v) => (mode.value = v)} small />
        <button class="btn primary big" type="submit" disabled={busy.value}>
          I'm in
        </button>
        <span class="hint">Others see a rough pin (~100 m).</span>
      </form>
    </Card>
  );
}

/** Each round starts without locations: "where are you starting from this time?" */
function WhereCard({ s, call }: { s: MeetupSnapshot; call: Call }) {
  const set = (p: Place) => call('/members/me', 'PATCH', { location: located(p) }).catch(fail);
  return (
    <Card class="join">
      <div class="stack">
        <h2>Where are you starting from?</h2>
        <GpsButton label="📍 I'm here (GPS)" onLocated={set} />
        <PlaceSearch placeholder="…or type an address" near={s.focus} onPick={set} />
        <span class="hint">Only people who add a starting point count for this round.</span>
      </div>
    </Card>
  );
}

function RateCard({ rate, call }: { rate: NonNullable<MeetupSnapshot['rate']>; call: Call }) {
  const send = (again: Again) =>
    call('/rating', 'POST', { round: rate.round, again }).then(() => toast('Thanks!'), fail);
  return (
    <Card>
      <div class="stack tight">
        <b>How was {rate.name}? Would you go again?</b>
        <div class="row">
          <button type="button" class="btn" onClick={() => send('yes')}>
            😍 Again!
          </button>
          <button type="button" class="btn ghost" onClick={() => send('maybe')}>
            😐 Maybe
          </button>
          <button type="button" class="btn ghost" onClick={() => send('no')}>
            👎 No
          </button>
        </div>
      </div>
    </Card>
  );
}

function Decided({ s, call }: { s: MeetupSnapshot; call: Call }) {
  const w = s.candidates.find((c) => c.id === s.decidedId);
  if (!w) return null;
  const me = s.me;
  const going = s.members.filter((m) => m.lat != null);
  const mode = GMAPS[me?.mode ?? 'transit'];
  return (
    <div class="winner">
      <span class="small">🎉 It's decided</span>
      <h2>{w.name}</h2>
      {w.address && <span>{w.address}</span>}
      <ul class="etas">
        {going.map((m) => (
          <li key={m.id}>
            <b>{m.name}</b> {modeEmoji(m.mode)} {w.times[m.id] != null ? `${mins(w.times[m.id])} away` : ''}
          </li>
        ))}
      </ul>
      <div class="row">
        <a
          class="btn"
          href={directionsUrl(w, me?.lat != null ? { lat: me.lat, lon: me.lon! } : null, mode)}
          target="_blank"
          rel="noopener"
        >
          Directions
        </a>
        <a class="btn ghost-light" href={nativeMapsUrl(w, w.name, mode)}>
          {nativeMapsName()}
        </a>
      </div>
      {me && (
        <div class="row">
          <button
            type="button"
            class="btn ghost-light"
            onClick={() => {
              if (confirm('Start over for next time? Places, votes and starting points are cleared.'))
                call('/restart', 'POST').catch(fail);
            }}
          >
            🔁 Next time: start over
          </button>
          <button type="button" class="linkbtn light" onClick={() => call('/reopen', 'POST').catch(fail)}>
            Change the pick
          </button>
        </div>
      )}
    </div>
  );
}

function ProposeBox({ s, call, onFly }: { s: MeetupSnapshot; call: Call; onFly: (p: LatLon) => void }) {
  return (
    <Card>
      <h3>Propose a place</h3>
      <PlaceSearch
        placeholder={`Search near ${s.focus?.label ?? 'the middle'}…`}
        near={s.focus}
        onPick={(p) => {
          onFly(p);
          const name = p.name || p.label.split(', ')[0];
          call('/candidates', 'POST', {
            name,
            lat: p.lat,
            lon: p.lon,
            address: p.label,
            extId: p.extId,
            category: p.kind ?? null,
          }).then(() => toast(`Proposed ${name}`), fail);
        }}
      />
    </Card>
  );
}

function Places({
  s,
  vote,
  call,
  hover,
}: {
  s: MeetupSnapshot;
  vote: (cid: string, v: -1 | 0 | 1) => void;
  call: Call;
  hover: { value: string | null };
}) {
  if (!s.candidates.length) {
    if (s.status !== 'open') return null;
    return (
      <Card>
        <p class="muted" style={{ margin: 0 }}>
          {s.members.some((m) => m.lat != null)
            ? s.activity
              ? 'Looking for places around the middle…'
              : 'No places yet. Propose one, or tap the map.'
            : 'Places appear once someone adds where they start from.'}
        </p>
      </Card>
    );
  }
  return (
    <div class="stack">
      {s.feynman && <FeynmanNote f={s.feynman} />}
      {s.candidates.map((c) => (
        <PlaceCard key={c.id} s={s} c={c} vote={vote} call={call} hover={hover} />
      ))}
    </div>
  );
}

function why(s: MeetupSnapshot, c: CandidateView) {
  const byId = new Map(s.members.map((m) => [m.id, m]));
  const worst = Object.entries(c.times).sort((a, b) => b[1] - a[1])[0];
  const parts = [
    `longest trip ${mins(c.maxTime)}${worst && byId.get(worst[0]) ? ` (${byId.get(worst[0])?.name})` : ''}`,
  ];
  if (c.up.length) parts.push(`${c.up.length} 👍`);
  if (c.pick) parts.push('your best so far 🧭');
  else if (s.feynman?.stance === 'explore' && !c.visited) parts.push('new to the group 🧭');
  return `${c.rank === 1 ? 'Fairest' : 'Why'}: ${parts.join(' · ')}`;
}

function PlaceCard({
  s,
  c,
  vote,
  call,
  hover,
}: {
  s: MeetupSnapshot;
  c: CandidateView;
  vote: (cid: string, v: -1 | 0 | 1) => void;
  call: Call;
  hover: { value: string | null };
}) {
  const everyone = useSignal(false);
  const me = s.me;
  const mine = me ? (c.up.includes(me.id) ? 1 : c.down.includes(me.id) ? -1 : 0) : 0;
  const byId = new Map(s.members.map((m) => [m.id, m]));
  const proposer = c.proposedBy ? byId.get(c.proposedBy) : null;
  const myTime = me ? c.times[me.id] : undefined;
  const open = s.status === 'open';
  const info = c.info as { rating?: number; price?: string; openNow?: boolean; website?: string; source?: string };
  const canRemove = open && !!me && (c.proposedBy === me.id || (c.source === 'auto' && !c.up.length));
  const rows = Object.entries(c.times).sort((a, b) => b[1] - a[1]);
  const mode = GMAPS[me?.mode ?? 'transit'];
  return (
    <article
      id={`c-${c.id}`}
      class={`place${c.id === s.decidedId ? ' win' : ''}${c.rank === 1 && open ? ' lead' : ''}${hover.value === c.id ? ' hl' : ''}`}
      onMouseEnter={() => (hover.value = c.id)}
      onMouseLeave={() => (hover.value = null)}
    >
      <header>
        <span class="rank">{c.id === s.decidedId ? '★' : c.rank}</span>
        <div class="grow">
          <h3>{c.name}</h3>
          <div class="sub">
            {[
              c.source === 'auto' ? 'suggested' : proposer ? `by ${proposer.name}` : 'proposed',
              c.category,
              c.address?.split(', ').slice(0, 2).join(', '),
              info.rating ? `★ ${info.rating.toFixed(1)}` : null,
              info.price,
              info.openNow === true ? 'open now' : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </div>
        </div>
        {canRemove && (
          <button
            type="button"
            class="x"
            title="Remove"
            onClick={() => call(`/candidates/${c.id}`, 'DELETE').catch(fail)}
          >
            ×
          </button>
        )}
      </header>
      {open && c.rank <= 3 && <div class="why small">{why(s, c)}</div>}
      {c.memory && (c.memory.yes || c.memory.no) ? (
        <div class="small muted">
          Last time: {c.memory.yes ? `😍 ${c.memory.yes}` : ''} {c.memory.maybe ? `😐 ${c.memory.maybe}` : ''}{' '}
          {c.memory.no ? `👎 ${c.memory.no}` : ''}
        </div>
      ) : null}
      <div class="times">
        {myTime != null && (
          <span class="mytime">
            {c.estimated ? '~' : ''}
            {mins(myTime)} <span class="muted">for you {me && modeEmoji(me.mode)}</span>
          </span>
        )}
        {rows.length > 0 && (
          <button type="button" class="linkbtn" onClick={() => (everyone.value = !everyone.value)}>
            everyone {everyone.value ? '▴' : '▾'}
          </button>
        )}
      </div>
      {everyone.value && (
        <ul class="time-rows">
          {rows.map(([mid, t]) => {
            const m = byId.get(mid);
            if (!m) return null;
            return (
              <li key={mid} class={t === c.maxTime ? 'max' : ''}>
                <span>{modeEmoji(m.mode)}</span>
                <span class="grow">{m.name}</span>
                <span>
                  {c.estimated ? '~' : ''}
                  {mins(t)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      <div class="foot">
        <div class="row">
          {open && me ? (
            <>
              <button
                type="button"
                class="vote up"
                aria-pressed={mine === 1}
                onClick={() => vote(c.id, mine === 1 ? 0 : 1)}
              >
                👍 {c.up.length || ''}
              </button>
              <button
                type="button"
                class="vote down"
                aria-pressed={mine === -1}
                onClick={() => vote(c.id, mine === -1 ? 0 : -1)}
              >
                👎 {c.down.length || ''}
              </button>
            </>
          ) : (
            <span class="small muted">
              👍 {c.up.length} · 👎 {c.down.length}
            </span>
          )}
        </div>
        {open && me && (
          <button
            type="button"
            class="btn sm good"
            onClick={() => {
              if (confirm(`Go to ${c.name}?`)) call('/decide', 'POST', { candidateId: c.id }).catch(fail);
            }}
          >
            Let's go here
          </button>
        )}
      </div>
      <div class="links">
        <a
          href={directionsUrl(c, me?.lat != null ? { lat: me.lat, lon: me.lon! } : null, mode)}
          target="_blank"
          rel="noopener"
        >
          Route
        </a>
        <a href={nativeMapsUrl(c, c.name, mode)}>{nativeMapsName()}</a>
        {c.url && (
          <a href={c.url} target="_blank" rel="noopener">
            {info.source ?? 'Details'}
          </a>
        )}
        {info.website && (
          <a href={info.website} target="_blank" rel="noopener nofollow">
            Website
          </a>
        )}
      </div>
    </article>
  );
}

function People({ s, call }: { s: MeetupSnapshot; call: Call }) {
  const editing = useSignal(false);
  const me = s.me;
  const setLoc = (p: Place) =>
    call('/members/me', 'PATCH', { location: located(p) }).then(() => {
      editing.value = false;
    }, fail);
  return (
    <Card>
      <h3>People</h3>
      <ul class="people-list">
        {s.members.map((m) => (
          <li key={m.id} class={m.lat == null ? 'inactive' : ''}>
            <Avatar name={m.name} color={m.color} small />
            <span class="grow">
              <b>{m.name}</b>
              {m.isMe && <span class="muted"> (you)</span>}
              <span class="small muted">
                {' '}
                · {m.lat == null ? 'no starting point yet' : m.isMe ? (me?.label?.split(', ')[0] ?? 'set') : 'in'}
              </span>
            </span>
            <span class="mode">{modeEmoji(m.mode)}</span>
          </li>
        ))}
      </ul>
      {me && (
        <div class="stack tight">
          <Chips
            options={config.value!.modes}
            value={me.mode}
            onChange={(v) => {
              savedMode.set(v);
              call('/members/me', 'PATCH', { travelMode: v }).catch(fail);
            }}
            small
          />
          {editing.value ? (
            <>
              <GpsButton label="📍 I'm here (GPS)" class="btn ghost" onLocated={setLoc} />
              <PlaceSearch placeholder="Where are you starting from?" near={s.focus} onPick={setLoc} />
              <button type="button" class="linkbtn" onClick={() => (editing.value = false)}>
                Cancel
              </button>
            </>
          ) : (
            me.lat != null && (
              <button type="button" class="linkbtn" onClick={() => (editing.value = true)}>
                Change my starting point
              </button>
            )
          )}
        </div>
      )}
    </Card>
  );
}

function MapSheet({
  place,
  s,
  onClose,
  onPropose,
  onStartHere,
}: {
  place: Place | null;
  s: MeetupSnapshot;
  onClose: () => void;
  onPropose: (name: string, p: Place) => void;
  onStartHere: (p: Place) => void;
}) {
  const name = useSignal('');
  // A new tap clears the name; the looked-up place name only fills it if you haven't typed one.
  useEffect(() => {
    name.value = '';
  }, [place?.lat, place?.lon]);
  useEffect(() => {
    if (!name.value && place?.name && place.name !== place.label) name.value = place.name;
  }, [place?.name]);
  if (!place || !s.me) return null;
  return (
    <Sheet open onClose={onClose} title={`📍 ${place.label.split(', ').slice(0, 2).join(', ')}`}>
      <div class="stack">
        {s.status === 'open' && (
          <div class="row">
            <input
              type="text"
              placeholder="Name this spot"
              value={name.value}
              onInput={(e) => (name.value = e.currentTarget.value)}
            />
            <button
              type="button"
              class="btn primary sm"
              onClick={() =>
                onPropose(
                  name.value.trim() ||
                    (place.label === LOOKING_UP ? 'Meet here' : `Meet at ${place.label.split(', ')[0]}`),
                  place,
                )
              }
            >
              Propose
            </button>
          </div>
        )}
        <button type="button" class="btn ghost" onClick={() => onStartHere(place)}>
          🏁 I'm starting from here
        </button>
      </div>
    </Sheet>
  );
}

function ShareCard({ s, call }: { s: MeetupSnapshot; call: Call }) {
  const url = `${location.origin}/m/${s.id}`;
  const acts = [...config.value!.activities, { id: 'none', label: 'Only our own ideas', emoji: '✋' }];
  const patch = (b: unknown) => call('', 'PATCH', b).catch(fail);
  return (
    <Card>
      <h3>Invite</h3>
      <div class="row">
        <input type="text" readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
        <ShareButton url={url} title={s.title} text={`${s.title}: where are you? Let's meet in the middle.`} />
      </div>
      <span class="hint">Same link every time: keep it in your group chat.</span>
      {s.me && (
        <details>
          <summary>Settings</summary>
          <div class="stack">
            <input
              type="text"
              value={s.title}
              maxLength={80}
              onChange={(e) => patch({ title: e.currentTarget.value })}
            />
            <Chips
              options={acts}
              value={s.activity ?? 'none'}
              onChange={(v) => patch({ activity: v === 'none' ? null : v })}
              small
            />
          </div>
        </details>
      )}
    </Card>
  );
}
